import { useState } from 'react'
import { crearUsuario } from '../db/usuarios'
import { useAuth } from '../context/AuthContext'
import { useConfig } from '../context/ConfigContext'

function reportarOnboarding(nombreTienda, telefono) {
  const meta = {
    idioma: navigator.language ?? null,
    zona: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
    esPwa: window.matchMedia('(display-mode: standalone)').matches,
    referrer: document.referrer || null,
  }
  fetch('https://admin.practiko.com.mx/api/caja/onboarding', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nombreTienda, telefono: telefono || null, ...meta }),
  }).catch(() => {})
}

export default function SetupAdmin({ onCreado }) {
  const { login } = useAuth()
  const { guardarConfig } = useConfig()
  const [paso, setPaso] = useState(1)
  const [nombreTienda, setNombreTienda] = useState('')
  const [telefono, setTelefono] = useState('')
  const [nombre, setNombre] = useState('')
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [userCreado, setUserCreado] = useState(null)  // almacena usuario hasta que se confirme paso 3

  function handlePaso1(e) {
    e.preventDefault()
    setError('')
    if (!nombreTienda.trim()) { setError('El nombre de la tienda es obligatorio'); return }
    setPaso(2)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!nombre.trim()) { setError('El nombre es obligatorio'); return }
    if (!/^\d{4}$/.test(pin)) { setError('El PIN debe ser de 4 dígitos'); return }
    if (pin !== pinConfirm) { setError('Los PINs no coinciden'); return }

    setGuardando(true)
    try {
      await guardarConfig({ nombreTienda: nombreTienda.trim() })
      const resultado = await crearUsuario(nombre.trim(), 'admin', pin)
      const user = { _id: resultado.id, nombre: nombre.trim(), rol: 'admin' }
      setUserCreado(user)   // guardar sin hacer login todavía — login en paso 3
      setPaso(3)
    } catch {
      setError('Error al crear el usuario')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="min-h-screen bg-tienda-bg flex flex-col items-center justify-center p-6 max-w-sm mx-auto">

      {/* Indicador de pasos */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((n, i) => (
          <div key={n} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
              ${paso > n
                ? 'bg-tienda-success text-gray-900'
                : paso === n
                  ? 'bg-tienda-accent text-gray-900'
                  : 'bg-tienda-surface border border-tienda-border text-tienda-muted'}`}>
              {paso > n ? '✓' : n}
            </div>
            {i < 2 && (
              <div className={`h-0.5 w-10 transition-colors ${paso > n ? 'bg-tienda-success' : 'bg-tienda-border'}`} />
            )}
          </div>
        ))}
      </div>

      {/* ── PASO 1: Nombre tienda ── */}
      {paso === 1 && (
        <>
          <div className="text-center mb-8">
            <p className="text-5xl mb-3">🛒</p>
            <h1 className="text-2xl font-bold text-tienda-text">¡Bienvenido!</h1>
            <p className="text-tienda-muted text-sm mt-1">¿Cómo se llama tu tienda?</p>
          </div>

          {/* Tip del paso */}
          <div className="w-full bg-tienda-accent/10 border border-tienda-accent/30 rounded-2xl p-4 mb-5 flex gap-3">
            <span className="text-xl flex-shrink-0">💡</span>
            <p className="text-tienda-muted text-xs leading-relaxed">
              Este nombre aparecerá en tus tickets de venta y en la app.
              Puedes cambiarlo después en Configuración.
            </p>
          </div>

          <form onSubmit={handlePaso1} className="w-full space-y-4">
            {error && (
              <div className="bg-tienda-danger/20 border border-tienda-danger/50 rounded-xl p-3 text-tienda-danger text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <label className="block text-tienda-muted text-sm mb-1">Nombre de la tienda</label>
              <input
                type="text"
                value={nombreTienda}
                onChange={e => setNombreTienda(e.target.value)}
                placeholder="Ej. Abarrotes La Esperanza"
                className="input-field"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-tienda-muted text-sm mb-1">
                Teléfono <span className="text-tienda-muted/60">(opcional)</span>
              </label>
              <input
                type="tel"
                inputMode="tel"
                value={telefono}
                onChange={e => setTelefono(e.target.value)}
                placeholder="Ej. 55 1234 5678"
                className="input-field"
              />
            </div>

            {nombreTienda.trim() && (
              <div className="card p-4 flex items-center gap-3">
                <span className="text-2xl">🛒</span>
                <span className="font-bold text-tienda-text">{nombreTienda.trim()}</span>
              </div>
            )}

            <button type="submit" className="btn-primary w-full py-3 text-base mt-2">
              Continuar →
            </button>
          </form>
        </>
      )}

      {/* ── PASO 2: Crear admin ── */}
      {paso === 2 && (
        <>
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-2xl">🛒</span>
              <span className="font-bold text-tienda-accent text-xl">{nombreTienda}</span>
            </div>
            <h1 className="text-2xl font-bold text-tienda-text">Crea tu usuario</h1>
            <p className="text-tienda-muted text-sm mt-1">Configura el administrador</p>
          </div>

          {/* Tip del paso */}
          <div className="w-full bg-tienda-accent/10 border border-tienda-accent/30 rounded-2xl p-4 mb-5 flex gap-3">
            <span className="text-xl flex-shrink-0">🔐</span>
            <p className="text-tienda-muted text-xs leading-relaxed">
              El PIN de 4 dígitos protege el acceso a la app.
              Guárdalo bien, lo necesitarás cada vez que inicies sesión.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="w-full space-y-4">
            {error && (
              <div className="bg-tienda-danger/20 border border-tienda-danger/50 rounded-xl p-3 text-tienda-danger text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <label className="block text-tienda-muted text-sm mb-1">Tu nombre</label>
              <input
                type="text"
                value={nombre}
                onChange={e => setNombre(e.target.value)}
                placeholder="Ej. Admin"
                className="input-field"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-tienda-muted text-sm mb-1">PIN (4 dígitos)</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                className="input-field tracking-widest text-center text-xl"
              />
            </div>

            <div>
              <label className="block text-tienda-muted text-sm mb-1">Confirmar PIN</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pinConfirm}
                onChange={e => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                className="input-field tracking-widest text-center text-xl"
              />
            </div>

            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => { setPaso(1); setError('') }}
                className="btn-ghost flex-1 py-3 text-base"
              >
                ← Atrás
              </button>
              <button
                type="submit"
                disabled={guardando}
                className="btn-primary flex-1 py-3 text-base"
              >
                {guardando ? 'Creando...' : 'Crear admin'}
              </button>
            </div>
          </form>
        </>
      )}

      {/* ── PASO 3: Primer producto ── */}
      {paso === 3 && (
        <>
          <div className="text-center mb-8">
            <p className="text-5xl mb-3">📦</p>
            <h1 className="text-2xl font-bold text-tienda-text">¡Listo, {nombre}!</h1>
            <p className="text-tienda-muted text-sm mt-1">Tu tienda está configurada</p>
          </div>

          {/* Tip del paso */}
          <div className="w-full bg-tienda-accent/10 border border-tienda-accent/30 rounded-2xl p-4 mb-6 flex gap-3">
            <span className="text-xl flex-shrink-0">🎯</span>
            <p className="text-tienda-muted text-xs leading-relaxed">
              <span className="text-tienda-accent font-semibold">Siguiente paso:</span> agrega tu primer
              producto en <strong className="text-tienda-text">Mis Productos</strong>. Sin productos en tu
              catálogo no podrás registrar ventas.
            </p>
          </div>

          <div className="w-full space-y-3">
            <button
              onClick={() => {
                reportarOnboarding(nombreTienda, telefono)
                login(userCreado)
                onCreado?.('/mis-productos')
              }}
              className="btn-primary w-full py-3 text-base"
            >
              Ir a Mis Productos →
            </button>
            <button
              onClick={() => {
                reportarOnboarding(nombreTienda, telefono)
                login(userCreado)
                onCreado?.('/')
              }}
              className="btn-ghost w-full py-2.5 text-sm"
            >
              Explorar la app primero
            </button>
          </div>
        </>
      )}
    </div>
  )
}
